import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Sparkles, Truck, Wand2 } from 'lucide-react';
import {
  ROUTE_ORDER, getWeekStart, addDays, ymd, fmtDayLabel, fmtDate,
  fmtKgShort, getLoadTone, toneBadgeClass, fmtDateLong, getRouteColorClass
} from './_utils';
import ReadOnlyBanner from './ReadOnlyBanner';

export default function WeeklyPlanner() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_planner') : true;
  const [weekStart, setWeekStart] = useState(() => getWeekStart());
  const [bookings, setBookings] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoPlanning, setAutoPlanning] = useState(null);
  const today = ymd(new Date());

  const fetchWeek = async () => {
    try {
      setLoading(true);
      const res = await api.get('/routeops/planner', { params: { week_start: ymd(weekStart) } });
      setBookings(res.data.bookings || []);
      setRoutes((res.data.routes || []).sort(
        (a, b) => ROUTE_ORDER.indexOf(a.key) - ROUTE_ORDER.indexOf(b.key)
      ));
    } catch (e) {
      toast.error('Failed to load planner');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeek();
  }, [weekStart]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Index bookings by day + route
  const grid = useMemo(() => {
    const map = {};
    for (const d of days) {
      const dstr = ymd(d);
      map[dstr] = { routes: {}, unassigned: [] };
      for (const r of routes) map[dstr].routes[r.key] = [];
    }
    for (const b of bookings) {
      if (!map[b.date_required]) continue;
      if (b.route_key && map[b.date_required].routes[b.route_key]) {
        map[b.date_required].routes[b.route_key].push(b);
      } else {
        map[b.date_required].unassigned.push(b);
      }
    }
    return map;
  }, [bookings, days, routes]);

  const counts = useMemo(() => {
    const u = bookings.filter(b => !b.route_key).length;
    const a = bookings.filter(b => b.route_key && b.status !== 'Completed').length;
    const ov = bookings.filter(b => b.status === 'Overweight').length;
    return { u, a, ov };
  }, [bookings]);

  const handleAutoPlan = async (dstr) => {
    try {
      setAutoPlanning(dstr);
      const res = await api.post('/routeops/planner/auto-plan', { date: dstr });
      toast.success(`Auto-planned ${res.data.assigned} deliveries for ${dstr}`);
      fetchWeek();
    } catch (e) {
      toast.error('Auto-plan failed');
    } finally {
      setAutoPlanning(null);
    }
  };

  const handleAssign = async (bookingId, routeKey) => {
    try {
      await api.put(`/routeops/bookings/${bookingId}`, { route_key: routeKey });
      fetchWeek();
    } catch (e) {
      toast.error('Assignment failed');
    }
  };

  const handleUnassign = async (bookingId) => {
    try {
      await api.put(`/routeops/bookings/${bookingId}`, { route_key: null });
      fetchWeek();
    } catch (e) {
      toast.error('Unassign failed');
    }
  };

  const weekEnd = addDays(weekStart, 6);

  return (
    <div className="space-y-4" data-testid="routeops-planner-page">
      {!canEdit && <ReadOnlyBanner pageName="Weekly Planner" />}
      {/* Topbar */}
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-slate-200">
        <h1 className="text-2xl font-bold text-slate-900">Weekly Planner</h1>
        <span className="text-xs text-slate-500 font-mono uppercase tracking-wide">
          W/C {fmtDateLong(weekStart)}
        </span>
        <div className="flex gap-2 ml-2">
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            {counts.u} Unassigned
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            {counts.a} Planned
          </Badge>
          {counts.ov > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              {counts.ov} Overweight
            </Badge>
          )}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
          <Button
            variant="ghost" size="sm"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="planner-prev-week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs font-mono px-2 text-slate-700">
            {fmtDate(weekStart)} – {fmtDate(weekEnd)}
          </span>
          <Button
            variant="ghost" size="sm"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="planner-next-week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setWeekStart(getWeekStart())}
            data-testid="planner-this-week"
          >
            Today
          </Button>
        </div>
      </div>

      {/* Week grid */}
      <div className="overflow-x-auto pb-4">
        <div className="grid grid-cols-7 gap-2 min-w-[1400px]">
          {days.map((d, di) => {
            const dstr = ymd(d);
            const isToday = dstr === today;
            const dayBucket = grid[dstr] || { routes: {}, unassigned: [] };
            return (
              <div
                key={dstr}
                className={`bg-white rounded-lg border ${
                  isToday ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200'
                } flex flex-col`}
                data-testid={`planner-day-${di}`}
              >
                {/* Day header */}
                <div className="px-2 py-2 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-lg">
                  <div>
                    <div className="text-sm font-bold text-slate-800">
                      {fmtDayLabel(d)}
                      {isToday && <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded px-1.5 py-0.5 font-mono">TODAY</span>}
                    </div>
                    <div className="text-[11px] font-mono text-slate-500">{fmtDate(d)}</div>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleAutoPlan(dstr)}
                    disabled={autoPlanning === dstr || dayBucket.unassigned.length === 0 || !canEdit}
                    className="h-7 px-2 text-[10px]"
                    data-testid={`planner-autoplan-${di}`}
                  >
                    <Wand2 className="h-3 w-3 mr-1" />
                    {autoPlanning === dstr ? '…' : 'Auto'}
                  </Button>
                </div>

                {/* Routes */}
                <div className="flex-1 divide-y divide-slate-100">
                  {routes.map((r) => {
                    const items = dayBucket.routes[r.key] || [];
                    const total = items.reduce((s, b) => s + (b.weight_kg || 0), 0);
                    const tone = getLoadTone(total, r.max_payload_kg);
                    // Heatmap: compute fill % and background color
                    const pct = r.max_payload_kg ? Math.min((total / r.max_payload_kg) * 100, 100) : 0;
                    const heatBg = pct === 0 ? 'transparent'
                      : pct > 100 ? 'rgba(231,76,60,0.18)'
                      : pct >= 90 ? `rgba(231,76,60,${0.05 + (pct - 90) * 0.013})`
                      : pct >= 70 ? `rgba(245,166,35,${0.04 + (pct - 70) * 0.004})`
                      : `rgba(46,204,113,${0.02 + pct * 0.0005})`;
                    return (
                      <div key={r.key} className="p-2 transition-colors" style={{ background: heatBg }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${getRouteColorClass(r.key)}`} />
                            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600">
                              {r.name.replace('HGV Route ', 'HGV')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Capacity bar */}
                            <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-raised, #1a2233)' }}>
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${Math.min(pct, 100)}%`,
                                  background: pct > 100 ? 'var(--red)' : pct >= 90 ? 'var(--amber)' : 'var(--green)',
                                }}
                              />
                            </div>
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${toneBadgeClass(tone)}`}>
                              {fmtKgShort(total)}/{fmtKgShort(r.max_payload_kg)}
                            </span>
                          </div>
                        </div>
                        {items.length === 0 ? (
                          <div className="text-[10px] italic text-slate-300 py-1.5 text-center">empty</div>
                        ) : (
                          <div className="space-y-1">
                            {items.map((b) => (
                              <div
                                key={b.id}
                                className={`rounded border text-[11px] px-1.5 py-1 flex items-center gap-1 ${
                                  b.status === 'Overweight'
                                    ? 'bg-red-50 border-red-200'
                                    : 'bg-emerald-50 border-emerald-200'
                                }`}
                                data-testid={`planner-slot-${b.id}`}
                              >
                                <span className="flex-1 truncate font-medium text-slate-800">{b.business_name}</span>
                                <span className="font-mono text-slate-600 w-6 text-center">{b.pallets}</span>
                                <span className="font-mono text-slate-600 w-10 text-right">{fmtKgShort(b.weight_kg)}</span>
                                {canEdit && (
                                  <button
                                    onClick={() => handleUnassign(b.id)}
                                    className="text-slate-400 hover:text-red-600 ml-0.5"
                                    title="Unassign"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Unassigned holding area */}
                {dayBucket.unassigned.length > 0 && (
                  <div className="p-2 border-t-2 border-dashed border-amber-300 bg-amber-50/50 rounded-b-lg">
                    <div className="text-[10px] uppercase font-bold text-amber-700 mb-1 tracking-wide">
                      Unassigned ({dayBucket.unassigned.length})
                    </div>
                    <div className="space-y-1">
                      {dayBucket.unassigned.map(b => (
                        <div
                          key={b.id}
                          className="rounded border border-amber-200 bg-white px-1.5 py-1 text-[11px]"
                          data-testid={`planner-unassigned-${b.id}`}
                        >
                          <div className="flex items-center gap-1">
                            <span className="flex-1 truncate font-medium text-slate-800">{b.business_name}</span>
                            <span className="font-mono text-slate-500 text-[9px]">{b.postcode}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="font-mono text-slate-600 text-[10px]">
                              {b.pallets}p · {fmtKgShort(b.weight_kg)}
                            </span>
                            <select
                              className="ml-auto text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white"
                              value=""
                              onChange={(e) => handleAssign(b.id, e.target.value)}
                              data-testid={`planner-assign-${b.id}`}
                              disabled={!canEdit}
                            >
                              <option value="">Assign…</option>
                              {routes.map(r => {
                                const total = (dayBucket.routes[r.key] || []).reduce((s, x) => s + x.weight_kg, 0);
                                const remain = r.max_payload_kg - total;
                                const fits = remain >= b.weight_kg;
                                return (
                                  <option key={r.key} value={r.key} disabled={!fits}>
                                    {r.name} ({fmtKgShort(remain)} {fits ? 'free' : 'over'})
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-center text-slate-400 text-sm py-4">Loading…</div>}
    </div>
  );
}
